/**
 * LoadingSkeleton component - Reusable skeleton loaders for various UI elements
 *
 * Features:
 * - Multiple skeleton variants (message, card, table, panel)
 * - Smooth pulse animation
 * - Consistent styling with space theme
 * - No layout shift during loading
 */

interface SkeletonProps {
  className?: string
}

/**
 * Base skeleton component with pulse animation
 */
function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse bg-dark-200 rounded ${className}`}
      role="status"
      aria-label="Loading..."
    />
  )
}

/**
 * Message skeleton - for chat messages
 */
export function MessageSkeleton() {
  return (
    <div className="bg-dark-100 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />

        {/* Content */}
        <div className="flex-1 space-y-2">
          {/* Header */}
          <Skeleton className="h-4 w-24" />

          {/* Text lines */}
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <Skeleton className="h-3 w-4/6" />
        </div>
      </div>
    </div>
  )
}

/**
 * Tool card skeleton - for workflow steps
 */
export function ToolCardSkeleton() {
  return (
    <div className="bg-dark-100 border border-dark-200 rounded-lg p-3 mb-2">
      <div className="flex items-center gap-3">
        {/* Icon */}
        <Skeleton className="w-5 h-5 rounded" />

        {/* Tool name */}
        <Skeleton className="h-4 w-32" />

        {/* Status badge */}
        <Skeleton className="h-5 w-20 ml-auto" />
      </div>
    </div>
  )
}

/**
 * Table skeleton - for data tables
 */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex gap-4 p-3 bg-dark-100 rounded-lg">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-4 flex-1" />
      </div>

      {/* Rows */}
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4 p-3">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-3 flex-1" />
        </div>
      ))}
    </div>
  )
}

/**
 * Chart/Plot skeleton - for visualizations
 */
export function ChartSkeleton() {
  return (
    <div className="bg-dark-100 rounded-lg border border-dark-200 p-6">
      <div className="space-y-4">
        {/* Title */}
        <Skeleton className="h-5 w-48" />

        {/* Chart area */}
        <div className="space-y-2">
          <Skeleton className="h-48 w-full" />

          {/* Legend */}
          <div className="flex gap-4 justify-center">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Panel skeleton - for artifact panels
 */
export function PanelSkeleton() {
  return (
    <div className="h-full p-6 space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-dark-200 pb-2">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
      </div>

      {/* Content */}
      <div className="space-y-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  )
}

/**
 * Connection skeleton - shown while establishing WebSocket connection
 */
export function ConnectionSkeleton() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 p-6">
      {/* Animated connection indicator */}
      <div className="relative">
        <Skeleton className="w-16 h-16 rounded-full" />
        <div className="absolute inset-0 rounded-full border-4 border-space-400 border-t-transparent animate-spin" />
      </div>

      {/* Text */}
      <div className="text-center space-y-2">
        <Skeleton className="h-4 w-48 mx-auto" />
        <Skeleton className="h-3 w-64 mx-auto" />
      </div>
    </div>
  )
}

/**
 * Workflow step skeleton - for in-progress workflow steps
 */
export function WorkflowStepSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-dark-100 rounded-lg">
          {/* Status icon */}
          <Skeleton className="w-5 h-5 rounded-full" />

          {/* Step info */}
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-2 w-24" />
          </div>

          {/* Progress bar */}
          <Skeleton className="h-1 w-24" />
        </div>
      ))}
    </div>
  )
}

/**
 * Inline loading spinner - for small loading states
 */
export function InlineSpinner({ className = '' }: { className?: string }) {
  return (
    <div
      className={`inline-block w-4 h-4 border-2 border-space-400 border-t-transparent rounded-full animate-spin ${className}`}
      role="status"
      aria-label="Loading..."
    />
  )
}

export default Skeleton
